import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { scrollToAnchor } from 'src/redux/pull-request/actions';
import { getActiveDiff } from 'src/redux/pull-request/selectors';
import { getFileTreeHrefs } from 'src/selectors/file-tree-selectors';

const useScrollToAnchor = () => {
  const dispatch = useDispatch();
  return useCallback(
    (anchorId: string) => {
      const element = document.getElementById(anchorId);
      if (element) {
        element.scrollIntoView();
        dispatch(scrollToAnchor(anchorId, true));
      } else {
        dispatch(scrollToAnchor(anchorId));
      }
    },
    [dispatch]
  );
};

export const useFileNavigation = (): ((
  fileIndex: number,
  orderedHrefs: string[],
  direction: 'next' | 'prev'
) => void) => {
  const scrollToAnchorId = useScrollToAnchor();

  return useCallback(
    (fileIndex: number, orderedHrefs: string[], direction: 'next' | 'prev') => {
      const newIndex = direction === 'next' ? fileIndex + 1 : fileIndex - 1;
      const newFileId = orderedHrefs[newIndex];
      if (newFileId) {
        const anchorId = newFileId.replace(/#/, '');
        scrollToAnchorId(anchorId);
      }
    },
    [scrollToAnchorId]
  );
};

export const useCurrentFileNavigation = (): ((
  direction: 'next' | 'prev'
) => void) => {
  const currentFile = useSelector(getActiveDiff);
  const orderedHrefs = useSelector(getFileTreeHrefs);

  const currentFileIndex = orderedHrefs.indexOf(`#${currentFile}`);
  const onFileNavigate = useFileNavigation();

  return useCallback(
    (direction: 'next' | 'prev') => {
      if (currentFileIndex > -1) {
        onFileNavigate(currentFileIndex, orderedHrefs, direction);
      }
    },
    [currentFileIndex, orderedHrefs, onFileNavigate]
  );
};
